import type React from 'react';
import { type FC, type ReactNode, useRef, type CSSProperties, useEffect, useCallback, memo, useLayoutEffect } from 'react';
import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { useContentOverflow } from '@creator/ui/hooks/useContentOverflow';
import { cn } from '@creator/ui/utils/ui';

interface OverflowableContentProps extends React.HTMLAttributes<HTMLDivElement> {
    contentId: string;
    enableExpend?: boolean;
    maxHeight: number;
    className?: string;
    children?: ReactNode;
    readMoreText?: string;
    onExpand?: () => void;
}

const OverflowableContent: FC<OverflowableContentProps> = props => {
    const { contentId, enableExpend = false, readMoreText, maxHeight, className = '', children, onExpand, ...rest } = props;
    const contentRef = useRef<HTMLDivElement>(null);
    const { isExpended, showReadMore, expendContent, isTruncatable } = useContentOverflow({ contentRef, contentId, enableExpend, maxHeight });

    useLayoutEffect(() => {
        if (!isExpended || !onExpand) return;
        onExpand();
    }, [isExpended]);

    function getReadMoreButtonWrapperClassName() {
        const base = `transition group 
            cursor-pointer absolute bottom-0 z-10 h-24 w-full bg-linear-to-t from-white dark:from-gray-950 to-transparent `;
        if (!isTruncatable) return base;

        return cn(base);
    }

    const wrapperStyle: CSSProperties = {};
    if (isTruncatable) {
        wrapperStyle.maxHeight = `${maxHeight}px`;
        wrapperStyle.overflow = 'hidden';
    }

    function renderReadMoreButton() {
        if (!isTruncatable || !showReadMore) return null;

        return (
            <div className={getReadMoreButtonWrapperClassName()} onClick={expendContent}>
                <Button className="absolute bottom-0 left-0 right-0 mx-auto" aria-expanded={isExpended} aria-controls={contentId} variant="ghost">
                    <Text className="">{readMoreText}</Text>
                </Button>
            </div>
        );
    }
    return (
        <div className={cn('relative', className)} {...rest}>
            <div data-ssr-remove-style="true" id={contentId} ref={contentRef} style={wrapperStyle}>
                {children}
            </div>
            {renderReadMoreButton()}
        </div>
    );
};

export default memo(OverflowableContent);
